import React, { useState } from 'react';

const URLScrapingPanel = ({ apiBaseUrl }) => {
  const [url, setUrl] = useState('');
  const [isScraping, setIsScraping] = useState(false);
  const [scrapingStatus, setScrapingStatus] = useState('');
  const [scrapedUrls, setScrapedUrls] = useState([]);

  const handleUrlScrape = async () => {
    if (!url.trim()) {
      setScrapingStatus('Please enter a valid URL');
      return;
    }

    setIsScraping(true);
    setScrapingStatus('Scraping and analyzing content from URL...');

    try {
      const response = await fetch(`${apiBaseUrl}/scrape-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url: url.trim() })
      });

      if (response.ok) {
        const result = await response.json();
        const analysisInfo = result.analysis ? 
          ` | PESTLE: ${result.analysis.pestle_category || 'N/A'} | Minerals: ${result.analysis.minerals?.length || 0} | Risk: ${result.analysis.risk_score || 'N/A'}` : '';
        setScrapingStatus(`✅ Successfully processed: ${result.filename}${analysisInfo}`);
        setScrapedUrls(prev => [...prev, { 
          url: url, 
          filename: result.filename, 
          timestamp: new Date().toLocaleString(),
          analysis: result.analysis
        }]);
        setUrl(''); // Clear the input
      } else {
        const error = await response.json();
        setScrapingStatus(`❌ Error: ${error.detail || 'Failed to scrape URL'}`);
      }
    } catch (error) {
      setScrapingStatus(`❌ Error: ${error.message}`);
    } finally {
      setIsScraping(false);
    }
  };

  const handleBatchScrape = async () => {
    const urls = url.split('\n').filter(u => u.trim());
    if (urls.length === 0) {
      setScrapingStatus('Please enter at least one valid URL');
      return;
    }

    setIsScraping(true);
    setScrapingStatus(`Scraping and analyzing ${urls.length} URLs...`);

    try {
      const response = await fetch(`${apiBaseUrl}/scrape-urls-batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ urls: urls })
      });

      if (response.ok) {
        const result = await response.json();
        const totalMinerals = result.processed_articles?.reduce((sum, article) => 
          sum + (article.analysis?.minerals?.length || 0), 0) || 0;
        setScrapingStatus(`✅ Successfully processed ${result.successful} out of ${urls.length} URLs | Total minerals detected: ${totalMinerals}`);
        setScrapedUrls(prev => [...prev, ...result.processed_articles?.map(article => ({ 
          url: article.url, 
          filename: article.filename, 
          timestamp: new Date().toLocaleString(),
          analysis: article.analysis
        })) || []]);
        setUrl(''); // Clear the input
      } else {
        const error = await response.json();
        setScrapingStatus(`❌ Error: ${error.detail || 'Failed to scrape URLs'}`);
      }
    } catch (error) {
      setScrapingStatus(`❌ Error: ${error.message}`);
    } finally {
      setIsScraping(false);
    }
  };

  const clearHistory = () => {
    setScrapedUrls([]);
    setScrapingStatus('');
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-200">
          🔗 URL Content Scraping
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Enter URLs to automatically scrape content and save to the scraped folder. 
          Supports single URLs or multiple URLs (one per line).
        </p>
      </div>

      {/* URL Input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          URL(s) to Scrape
        </label>
        <textarea
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Enter a single URL or multiple URLs (one per line)&#10;Example:&#10;https://example.com/article1&#10;https://example.com/article2"
          className="w-full h-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
          disabled={isScraping}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex space-x-3">
        <button
          onClick={handleUrlScrape}
          disabled={isScraping || !url.trim()}
          className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
            isScraping || !url.trim()
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
          }`}
        >
          {isScraping ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Scraping...</span>
            </div>
          ) : (
            <div className="flex items-center justify-center space-x-2">
              <span>🔗</span>
              <span>Scrape Single URL</span>
            </div>
          )}
        </button>

        <button
          onClick={handleBatchScrape}
          disabled={isScraping || !url.trim()}
          className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
            isScraping || !url.trim()
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-green-600 text-white hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2'
          }`}
        >
          {isScraping ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Scraping...</span>
            </div>
          ) : (
            <div className="flex items-center justify-center space-x-2">
              <span>📋</span>
              <span>Scrape Multiple URLs</span>
            </div>
          )}
        </button>
      </div>

      {/* Status */}
      {scrapingStatus && (
        <div className={`p-3 rounded-lg text-sm ${
          scrapingStatus.includes('✅') 
            ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200'
            : scrapingStatus.includes('❌')
            ? 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'
            : 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200'
        }`}>
          {scrapingStatus}
        </div>
      )}

      {/* Scraped URLs History */}
      {scrapedUrls.length > 0 && (
        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-gray-800 dark:text-gray-200">
              Recently Scraped URLs ({scrapedUrls.length})
            </h4>
            <button
              onClick={clearHistory}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Clear History
            </button>
          </div>
                     <div className="space-y-2 max-h-40 overflow-y-auto">
             {scrapedUrls.slice(-5).reverse().map((item, index) => (
               <div key={index} className="text-sm text-gray-600 dark:text-gray-400 border-l-2 border-green-500 pl-3">
                 <div className="font-medium truncate">{item.filename}</div>
                 <div className="text-xs text-gray-500 truncate">{item.url}</div>
                 {item.analysis && (
                   <div className="text-xs text-blue-600 dark:text-blue-400">
                     PESTLE: {item.analysis.pestle_category || 'N/A'} | 
                     Minerals: {item.analysis.minerals?.length || 0} | 
                     Risk: {item.analysis.risk_score || 'N/A'}
                   </div>
                 )}
                 <div className="text-xs text-gray-400">{item.timestamp}</div>
               </div>
             ))}
           </div>
        </div>
      )}

             {/* Info */}
       <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
         <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
           🔗 Enhanced URL Processing
         </h4>
         <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
           <li>• <strong>Content Scraping:</strong> Automatically extracts article content and metadata</li>
           <li>• <strong>NLP Analysis:</strong> Processes through critical minerals analyzer</li>
           <li>• <strong>Database Storage:</strong> Saves to both Pinecone and local SQLite database</li>
           <li>• <strong>File Storage:</strong> HTML and text files saved to <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">uploads/scraped/</code></li>
           <li>• <strong>Analysis Results:</strong> PESTLE categories, minerals, countries, risk scores</li>
         </ul>
       </div>
    </div>
  );
};

export default URLScrapingPanel; 